import { Component, HostListener, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { NgxSpinnerModule } from 'ngx-spinner';
import { Commonservice } from '../services/commonservice';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, NgxSpinnerModule],
  templateUrl: './app.html',
  styleUrl: './app.scss'
})
export class App {
  protected readonly commonSvc = inject(Commonservice);

  @HostListener('document:click', ['$event'])
  protected handleDocumentClick(event: MouseEvent): void {
    const activeMenu = this.getActiveActionMenu(event.target);
    const activeDetails = this.getActiveOpenDetails(event.target);
    this.closeOpenActionMenus(activeMenu, activeDetails);

    if (activeMenu) {
      window.requestAnimationFrame(() => this.adjustActionMenuPosition(activeMenu));
    }
  }

  @HostListener('document:keydown.escape')
  protected handleEscapeKey(): void {
    this.closeOpenActionMenus(null, null);
  }

  @HostListener('window:resize')
  protected handleWindowResize(): void {
    this.adjustOpenActionMenus();
  }

  @HostListener('document:scroll')
  protected handleDocumentScroll(): void {
    document.querySelectorAll<HTMLInputElement>('input[type="date"]:focus').forEach((input) => input.blur());
    this.adjustOpenActionMenus();
  }

  @HostListener('window:scroll')
  protected handleWindowScroll(): void {
    const focusedDateInputs = document.querySelectorAll<HTMLInputElement>('input[type="date"]:focus');
    focusedDateInputs.forEach((input) => input.blur());
    this.adjustOpenActionMenus();
  }

  private getActiveActionMenu(target: EventTarget | null): HTMLDetailsElement | null {
    if (!(target instanceof Element)) {
      return null;
    }

    const item = target.closest('.app-row-action-menu__item');
    if (item) {
      return null;
    }

    const summary = target.closest('.app-row-action-menu__toggle');
    if (!summary) {
      return null;
    }

    const menu = summary.closest('details.app-row-action-menu__details');
    return menu instanceof HTMLDetailsElement ? menu : null;
  }

  private getActiveOpenDetails(target: EventTarget | null): HTMLDetailsElement | null {
    if (!(target instanceof Element)) {
      return null;
    }

    const details = target.closest('details[open]');
    return details instanceof HTMLDetailsElement ? details : null;
  }

  private closeOpenActionMenus(
    activeMenu: HTMLDetailsElement | null,
    activeDetails: HTMLDetailsElement | null,
  ): void {
    if (typeof document === 'undefined') {
      return;
    }

    const openMenus = Array.from(document.querySelectorAll<HTMLDetailsElement>('details[open]'));

    for (const menu of openMenus) {
      if (menu === activeMenu || menu === activeDetails) {
        continue;
      }

      menu.open = false;
    }
  }

  private adjustOpenActionMenus(): void {
    if (typeof document === 'undefined') {
      return;
    }

    const openMenus = Array.from(document.querySelectorAll<HTMLDetailsElement>('details.app-row-action-menu__details[open]'));
    for (const menu of openMenus) {
      this.adjustActionMenuPosition(menu);
    }
  }

  private adjustActionMenuPosition(menu: HTMLDetailsElement): void {
    const dropdown = menu.querySelector<HTMLElement>('.app-row-action-menu__dropdown');
    const toggle = menu.querySelector<HTMLElement>('.app-row-action-menu__toggle');
    if (!dropdown) {
      return;
    }

    dropdown.classList.remove('app-row-action-menu__dropdown--dropup');

    const anchorRect = (toggle ?? dropdown).getBoundingClientRect();
    const dropdownHeight = dropdown.getBoundingClientRect().height;
    const gap = 8;
    const scrollContainer = menu.closest('.app-table-scroll, [class$="__table-wrap"]');
    const containerRect = scrollContainer?.getBoundingClientRect();
    const boundaryBottom = containerRect?.bottom ?? window.innerHeight ?? document.documentElement.clientHeight;
    const boundaryTop = containerRect?.top ?? 0;

    const spaceBelow = boundaryBottom - anchorRect.bottom;
    const spaceAbove = anchorRect.top - boundaryTop;

    if (spaceBelow < dropdownHeight + gap && spaceAbove > dropdownHeight + gap) {
      dropdown.classList.add('app-row-action-menu__dropdown--dropup');
    }
  }
}
